import { Injectable } from '@angular/core';

import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, map } from 'rxjs/operators';

import { EnvironmentService, EventService, ListService, fromList } from '@core/services';
import { currentTimeInSeconds } from '@util/list';

import { MultipleListOfValuesResponse } from '#types/list';

import { CONSTANTS } from '@user/constants';
import { UserService } from '@user/services';
import * as UserActions from '@user/state/user.actions';

@Injectable()
export class UserEffects {
  constructor(
    private actions$: Actions,
    private store: Store,
    private userService: UserService,
    private environmentService: EnvironmentService,
    private listService: ListService,
    private eventService: EventService
  ) {}

  RequestLists$ = createEffect(
    () => {
      return this.actions$.pipe(
        ofType(UserActions.RequestLists),
        concatLatestFrom(() => [this.store.select(fromList.selectUserLists)]),
        map(([_, lists]) => {
          let lastUpdated = -1;

          for (const list of Object.keys(lists)) {
            // skip parameterized lists
            if (lists[list] && !Array.isArray(lists[list].data)) {
              continue;
            }

            if (lists[list]) {
              lastUpdated = Math.max(lastUpdated, currentTimeInSeconds() - (lists[list].updated ?? 0));
            }
          }

          if (lastUpdated === -1 || lastUpdated > this.environmentService.settings.secondsToCacheLists) {
            this.store.dispatch(UserActions.RequestUserLists());
          }
        })
      );
    },
    { dispatch: false }
  );

  requestUserLists$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UserActions.RequestUserLists),
      exhaustMap(() => {
        return this.listService.getListOfValues<MultipleListOfValuesResponse>('user').pipe(
          this.eventService.indicate(CONSTANTS.LIST_ALL),
          map((lists) => UserActions.RequestUserListsSuccess({ lists })),
          catchError((error) => of(UserActions.RequestUserListsFailure(error)))
        );
      })
    );
  });
}
